// What a person is told about a run (decision 13): exactly four states, in
// these words — Provisioning, Running, Done, Failed. Progress only when the
// action reports it. The finer steps (starting, preparing, saving…) stay in the
// run record for logs and the admin page.

import type { Action, PermittedUse, Run, RunState } from './types';

export const STATE_WORDS: Record<RunState, string> = {
  provisioning: 'Provisioning',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
};

/**
 * The state shown for a run, or null for one that is not shown (cancelled).
 * The server's `state` is the answer; status and step are read only when a
 * run arrives without one, by the contract's table.
 */
export function runState(run: Pick<Run, 'state' | 'status' | 'step'>): RunState | null {
  if (run.status === 'cancelled' || run.state === 'cancelled') return null;
  if (run.state && run.state in STATE_WORDS) return run.state;
  switch (run.status) {
    case 'queued':
      return 'provisioning';
    case 'running':
      return run.step === 'running' || run.step === 'saving' ? 'running' : 'provisioning';
    case 'succeeded':
      return 'done';
    case 'failed':
      return 'failed';
    default:
      return null;
  }
}

/** Still on its way: Provisioning or Running. */
export function isActive(run: Pick<Run, 'state' | 'status' | 'step'>): boolean {
  const s = runState(run);
  return s === 'provisioning' || s === 'running';
}

/** "Running 42%" only when the action reported progress; otherwise the bare word. */
export function stateLabel(run: Pick<Run, 'state' | 'status' | 'step' | 'progress'>): string {
  const s = runState(run);
  if (!s) return '';
  if (s === 'running' && typeof run.progress === 'number' && Number.isFinite(run.progress)) {
    const pct = Math.round(Math.max(0, Math.min(1, run.progress)) * 100);
    return `Running ${pct}%`;
  }
  return STATE_WORDS[s];
}

/**
 * The sections actions are shown in, in their order. We own the names and
 * their order; an action's `category` picks one, and any other category is
 * Analysis, after them all.
 */
export const CATEGORIES: ReadonlyArray<readonly [category: string, title: string]> = [
  ['quality-control', 'Quality control'],
  ['biomarker-scoring', 'Biomarker scoring'],
  ['cancer-detection', 'Cancer detection'],
  ['grading', 'Grading and measurement'],
  ['cells-and-tissue', 'Cells and tissue'],
  ['prediction', 'Prediction from H&E'],
  ['specialties', 'Specialties'],
  ['assistants', 'Assistants and search'],
  ['image-tools', 'Image tools'],
];

/** The section an action is shown in. */
export function categoryTitle(category: string | null | undefined): string {
  return CATEGORIES.find(([c]) => c === category)?.[1] ?? 'Analysis';
}

/** Sections in their order: Quality control first, Analysis last. */
export function categoryRank(category: string | null | undefined): number {
  const i = CATEGORIES.findIndex(([c]) => c === category);
  return i < 0 ? CATEGORIES.length : i;
}

/**
 * Items under their category's section, Quality control first, each section's
 * items by name; or, without `nameOf`, in the order they came.
 */
export function sectionsByCategory<T>(
  items: readonly T[],
  categoryOf: (item: T) => string | null | undefined,
  nameOf?: (item: T) => string,
): Array<{ title: string; entries: T[] }> {
  const sections = new Map<string, { rank: number; entries: T[] }>();
  for (const item of items) {
    const title = categoryTitle(categoryOf(item));
    if (!sections.has(title)) sections.set(title, { rank: categoryRank(categoryOf(item)), entries: [] });
    sections.get(title)!.entries.push(item);
  }
  return [...sections]
    .sort((a, b) => a[1].rank - b[1].rank || a[0].localeCompare(b[0]))
    .map(([title, s]) => ({ title, entries: nameOf ? s.entries.sort((a, b) => nameOf(a).localeCompare(nameOf(b))) : s.entries }));
}

const USE_WORDS: Record<string, string> = { research: 'Research', education: 'Education', clinical: 'Clinical' };

/** "Research use only" for a research action; otherwise the uses it is permitted for. */
export function permittedUseLabel(uses: PermittedUse[] | null | undefined): string {
  const list = uses ?? [];
  if (list.length === 1 && list[0] === 'research') return 'Research use only';
  if (list.length === 0) return '';
  const words = list.map((u) => USE_WORDS[u] ?? u);
  const joined = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1].toLowerCase()}`;
  return `${joined.charAt(0).toUpperCase()}${joined.slice(1).toLowerCase()} use`;
}

/**
 * Who asked for a run, in words: "you" for the reader's own, the person's
 * name when the server sends one, else null. The contract sends only the
 * person's user id, which is no one's name.
 */
export function requesterName(run: Pick<Run, 'requestedBy' | 'requestedByName'>, me?: string | null): string | null {
  if (!run.requestedBy) return null;
  if (me && run.requestedBy === me) return 'you';
  return run.requestedByName?.trim() || null;
}

/** "2 Oct 2026, 14:03" in the reader's locale. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** "Ran automatically · 2 Oct 2026, 14:03", "Run by you · …", "Run on request · …". */
export function provenanceLine(run: Run, me?: string | null): string {
  const when = formatWhen(run.finishedUtc ?? run.startedUtc ?? run.createdUtc);
  const who = requesterName(run, me);
  const how = who ? `Run by ${who}` : run.requestedBy ? 'Run on request' : 'Ran automatically';
  return when ? `${how} · ${when}` : how;
}

/** Whether a person may start this action from the viewer. */
export function runsOnDemand(action: Pick<Action, 'triggers'>): boolean {
  return !action.triggers || action.triggers.includes('on-demand');
}

/** Whether the image's workspace turned it on. A granted action is off until its administrators turn it on. */
export function isEnabled(action: Pick<Action, 'enabled'>): boolean {
  return action.enabled === true;
}
